import type { CustomValidateFunction } from '@/components/forms/types';
import { type DefinedError } from 'ajv';
import type { AnyValidateFunction } from 'ajv/dist/core';
import type { FormOptions, GenericObject, TypedSchema, TypedSchemaError } from 'vee-validate';
import {
  additionalPropertiesStrategy,
  anyOfStrategy,
  constStrategy,
  formatStrategy,
  genericStrategy,
  minItemsStrategy,
  oneOfStrategy,
  patternStrategy,
  requiredStrategy,
  typeStrategy,
} from './errorStrategies';
import type { ErrorStrategy } from './types';
import { Ref } from 'vue';
import pDebounce from 'p-debounce';
import { t } from 'i18next';
import { truthy } from '@/utils/helper';
import { groupBy, isEmpty, isUndefined } from 'lodash';

export interface AggregatedAjvError {
  path: string;
  errors: string[];
}

const errorStrategyMap = new Map<string, ErrorStrategy | null>();
errorStrategyMap.set('additionalProperties', additionalPropertiesStrategy);
errorStrategyMap.set('anyOf', anyOfStrategy);
errorStrategyMap.set('oneOf', oneOfStrategy);
errorStrategyMap.set('enum', genericStrategy);
errorStrategyMap.set('format', formatStrategy);
errorStrategyMap.set('exclusiveMinimum', genericStrategy);
errorStrategyMap.set('exclusiveMaximum', genericStrategy);
errorStrategyMap.set('minLength', genericStrategy);
errorStrategyMap.set('maxLength', genericStrategy);
errorStrategyMap.set('minimum', genericStrategy);
errorStrategyMap.set('maximum', genericStrategy);
errorStrategyMap.set('required', requiredStrategy);
errorStrategyMap.set('type', typeStrategy);
errorStrategyMap.set('minItems', minItemsStrategy);
errorStrategyMap.set('nominalHv', genericStrategy);
errorStrategyMap.set('pattern', patternStrategy);
errorStrategyMap.set('discriminator', genericStrategy);
errorStrategyMap.set('if', null);
errorStrategyMap.set('const', constStrategy);
errorStrategyMap.set('not', genericStrategy);

function isIndex(value: unknown): value is number {
  return Number(value) >= 0;
}

interface ValidationSchemaProps<T extends GenericObject> {
  validateFunction?: AnyValidateFunction;
  customValidateFunctions?: CustomValidateFunction<T>[];
  uploadsRunning?: Ref<Set<string>>;
  uploadErrors?: Ref<Set<string>>;
  debounceWait?: number;
  skipValidation: Ref<boolean>;
  validationPriority: 'prioritizeSchemaValidation' | 'prioritizeCustomValidation';
  options?: FormOptions<T>;
}

async function schemaValidation<T>(
  validateFunction: AnyValidateFunction,
  values: T,
): Promise<TypedSchemaError[]> {
  const isValid = await validateFunction(values);

  if (!isValid && validateFunction.errors) {
    return (validateFunction.errors as DefinedError[])
      .map((error) => {
        const { keyword, params, instancePath } = error;
        const validationStrategy = errorStrategyMap.get(keyword);

        if (validationStrategy === undefined) {
          // Keywords without a validation strategy are ignored. This is on purpose as some messages
          // don't make much sense to be displayed in the UI, e.g. "if" errors if the sub-schema
          // validation fails as there will be other messages relating to the properties actually
          // causing the error(s).
          // throw new Error(`no validation strategy defined for: ${keyword}`);
        } else if (validationStrategy !== null) {
          // instancePath references the object which failed the validation. Especially for the 'required'
          // validation this can be confusing, because it will not reference the missing property, but
          // the parent object which is missing the property. In our case this is usually the root
          // object, thus instancePath will be an empty string.
          // This is the reason why we can not always derive the failed property from instancePath, but
          // might have to look into the params object, e.g. the 'required' validation will mention the
          // missing property there.
          const { path, message } = validationStrategy({
            instancePath,
            keyword,
            params,
          });

          // match to VeeValidate Paths: "/propertyEventLogs/0/lastModificationTime" --> "propertyEventLogs[0].lastModificationTime"
          const newPath = path
            .substring(1)
            .split('/')
            .reduce((prev, curr) => {
              if (isIndex(curr)) {
                return prev + `[${curr}]`;
              } else {
                return prev + (prev ? '.' : '') + curr;
              }
            }, '');

          return { path: newPath, errors: [message] };
        }
      })
      .filter(truthy);
  } else {
    return [];
  }
}

async function customValidation<T>(
  customValidateFunctions: CustomValidateFunction<T>[],
  values: T,
): Promise<TypedSchemaError[]> {
  return (
    await Promise.all(
      customValidateFunctions.map(
        async (customValidateFunction) => (await customValidateFunction(values)) ?? [],
      ),
    )
  ).flat();
}

async function uploadValidation(
  uploadsRunning?: Set<string>,
  uploadErrors?: Set<string>,
): Promise<TypedSchemaError[]> {
  const running = uploadsRunning?.size
    ? Array.from(uploadsRunning).map((path) => ({
        path,
        errors: [t('UploadInProgressMsg')],
      }))
    : [];

  const uploads = uploadErrors?.size
    ? Array.from(uploadErrors).map((path) => ({
        path,
        errors: [t('UploadFailedMsg')],
      }))
    : [];

  return [...running, ...uploads];
}

function logValidation(errors: Record<string, TypedSchemaError[]>) {
  const hasAnyErrors = !!Object.values(errors).flat().length;

  if (hasAnyErrors) {
    console.trace('Validated: BAD');

    Object.entries(errors).forEach(([group, errors]) => {
      errors.forEach((error) => {
        console.debug(`[${group}] validation error "${error.path}":`, error.errors);
      });
    });
  } else {
    console.info('Validated: GOOD');
  }
}

function groupErrors(errors: TypedSchemaError[]) {
  const groups = groupBy(errors, (error) => error.path);

  return Object.entries(groups).map(([path, errors]) => ({
    path,
    errors: errors.flatMap((error) => error.errors),
  }));
}

// inspired by Zod Schema Validation: https://github.com/logaretm/vee-validate/blob/main/packages/zod/src/index.ts
export const createValidationSchema = <T extends GenericObject>({
  validateFunction,
  customValidateFunctions,
  debounceWait = 250,
  skipValidation,
  uploadsRunning,
  uploadErrors,
  validationPriority,
  options,
}: ValidationSchemaProps<T>): TypedSchema<T> => {
  return {
    __type: 'VVTypedSchema',
    parse: pDebounce(async function (values: T) {
      // initialValues is undefined and when values is empty, we skip validation
      const skipValidationEmptyData = isEmpty(values) && isUndefined(options?.initialValues);
      if (skipValidation.value || skipValidationEmptyData) {
        return { errors: [], value: values };
      }

      const schemaValidationErrors = validateFunction
        ? await schemaValidation(validateFunction, values)
        : [];

      const customValidationErrors = customValidateFunctions
        ? await customValidation(customValidateFunctions, values)
        : [];

      const uploadValidationErrors =
        (await uploadValidation(uploadsRunning?.value, uploadErrors?.value)) ?? [];

      const errors = groupErrors(
        validationPriority === 'prioritizeCustomValidation'
          ? [...customValidationErrors, ...uploadValidationErrors, ...schemaValidationErrors]
          : [...schemaValidationErrors, ...customValidationErrors, ...uploadValidationErrors],
      );

      logValidation({
        schema: schemaValidationErrors,
        custom: customValidationErrors,
        upload: uploadValidationErrors,
      });

      return { errors, value: values };
    }, debounceWait),
  };
};
